import {
  faCheck,
  faCog,
  faFileUpload,
  faRotate,
  faTrash,
  faTriangleExclamation,
  faX,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AdvancedSelect,
  BadgeIcon,
  Button,
  Input,
  LoadingBlock,
  Table,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import axios from 'axios'
import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal'
import FiltersModal from '../../components/FiltersModal/FiltersModal'
import { SortButton } from '../../components/SortButton/SortButton'
import { useDispatch, useStore } from '../../store/store'
import type {
  Product,
  ProductType,
  TrainingStatus,
} from '../../store/store.types'
import {
  deleteProduct,
  emptyProducts,
  fetchProducts,
  setProduct,
} from '../../store/storeActions'
import { Sorting, SortingDirection } from '../../types/sorting'
import { handleApiErrors } from '../../utils/apiErrors'
import { formatDate } from '../../utils/date'
import { getEnvs } from '../../utils/env'
import { locations } from '../../utils/locations'
import { getProductClassesList, getProductTypeName } from '../../utils/products'
import { serializeSorting } from '../../utils/sorting'
import { DownloadAnnotationsButton } from './DownloadAnnotationsButton'
import ProductsCreateUpdate from './ProductsCreateUpdate'
import useStyles from './ProductsList.styles'
import UploadAnnotationClassMappingModal from './UploadAnnotationClassMappingModal/UploadAnnotationClassMappingModal'
import UploadAnnotationOnlyObservationModal from './UploadAnnotationOnlyObservationModal/UploadAnnotationOnlyObservationModal'

interface RouteParams {
  projectId: string
}

const itemsPerPage = 50

interface ProductColumns extends Product {
  actions?: string
}

type ProductFilters = {
  name?: string
  type?: string
}

const ProductsList = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { products } = useStore()
  const dispatch = useDispatch()
  const { projectId } = useParams<RouteParams>()
  const [isDeleteSubmitting, setDeleteSubmitting] = useState(false)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductColumns>()
  const [productsCreateUpdateModal, setProductsCreateUpdateModal] = useState(
    false
  )
  const classes = useStyles()
  const [, setIsLoading] = useState(false)
  const [isAnnotationsImportPending, setIsAnnotationsImportPending] = useState(
    false
  )
  const { apiUrl } = getEnvs()
  const [showClassMapping, setShowClassMapping] = useState(false)
  const [showOnlyObsDisclaimer, setShowOnlyObsDisclaimer] = useState(false)
  const [annotationFileToUpload, setAnnotationFileToUpload] = useState<File>()
  const [selectedProductId, setSelectedProductId] = useState(-1)

  const [lastDescriptionUpdated, setLastDescriptionUpdated] = useState({
    productId: -1,
    description: '',
  })

  const [sorting, setSorting] = useState<Sorting>({})
  const sortingRef = useRef<Sorting>({})
  const [productFilters, setProductFilters] = useState<ProductFilters>({})
  const productFiltersRef = useRef<ProductFilters>({})
  const [listKey, setListKey] = useState(0)

  const getDetailsLocation = (prjId: number, productId: number) =>
    locations.trainings(Number(prjId), productId)

  const serializeFilters = (f: ProductFilters): string => {
    const parts: string[] = []
    if (f.name) parts.push(`name=${encodeURIComponent(f.name)}`)
    if (f.type) parts.push(`type=${encodeURIComponent(f.type)}`)
    return parts.length ? `&${parts.join('&')}` : ''
  }

  const resetList = () => {
    emptyProducts(dispatch)
    setListKey((k) => k + 1)
  }

  const handleLoadMore = (page: number) => {
    const orderingParam = serializeSorting(sortingRef.current) || undefined
    const filterParam = serializeFilters(productFiltersRef.current) || undefined
    fetchProducts(
      dispatch,
      page !== 0,
      Number(projectId),
      itemsPerPage,
      page * itemsPerPage,
      orderingParam,
      filterParam
    )
  }

  const handleSort = (key: string, direction: SortingDirection) => {
    const newSorting = { ...sortingRef.current, [key]: direction }
    sortingRef.current = newSorting
    setSorting(newSorting)
    resetList()
  }

  const handleSaveFilters = (f: Partial<ProductFilters>) => {
    productFiltersRef.current = f as ProductFilters
    setProductFilters(f as ProductFilters)
    resetList()
  }

  const handleRemoveFilters = () => {
    productFiltersRef.current = {}
    setProductFilters({})
    resetList()
  }

  const filterLabels: { [key in keyof ProductFilters]: string } = {
    name: t('Name'),
    type: t('Type'),
  }

  const handleSubmit = async (productId: string, file?: File) => {
    if (file === undefined) {
      toast.error(t('Error during annotations import'))
      return
    }
    toast.success(t('Import started'))
    setIsLoading(true)
    setIsAnnotationsImportPending(true)
    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('product_id', productId)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    axios({
      method: 'post',
      url: `${apiUrl}/aigo/datatransfer/import/`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(() => {
        toast.success(t('File successfully imported'))
      })
      .catch(handleApiErrors)
      .finally(() => {
        setIsLoading(false)
        setIsAnnotationsImportPending(false)
      })
  }

  const [srcProductClasses, setSrcProductClasses] = useState<Array<string>>()
  const [trgProductClasses, setTrgProductClasses] = useState<Array<string>>()

  const handleCheckCompatibility = async (file: File, productId: string) => {
    if (file === undefined) {
      toast.error(t('Error during annotations import'))
      return
    }
    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('product_id', productId)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    axios({
      method: 'post',
      url: `${apiUrl}/aigo/datatransfer/checkcompatibility/`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((response) => {
        const { compatibility } = response.data
        const src: Array<string> = response.data.source_product_classes
        const trg: Array<string> = response.data.target_product_classes

        setSrcProductClasses(src)
        setTrgProductClasses(trg)

        if (String(compatibility) === 'only_observations') {
          setShowOnlyObsDisclaimer(true)
        } else if (String(compatibility) === 'class_mapping_required') {
          setShowClassMapping(true)
        } else {
          handleSubmit(productId, file)
        }
      })
      .catch(handleApiErrors)
  }

  const handleFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    productId: string
  ) => {
    if (!e.target.files) {
      return
    }
    const file: File = e.target.files[0]
    setAnnotationFileToUpload(file)
    setSelectedProductId(Number(productId))
    handleCheckCompatibility(file, productId)
  }

  const inputRef = useRef<HTMLInputElement | null>(null)

  const renderStatusBadge = (
    statusCounters: { [key in TrainingStatus]: number }
  ) => (
    <>
      {statusCounters.completed !== 0 && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faCheck} />}
          title={t('Completed')}
          badge={statusCounters.completed.toString()}
          size="xs"
        />
      )}
      {statusCounters.running !== 0 && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faRotate} />}
          title={t('Running')}
          badge={statusCounters.running.toString()}
          size="xs"
        />
      )}
      {statusCounters.error !== 0 && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
          title={t('Error')}
          badge={statusCounters.error.toString()}
          size="xs"
        />
      )}
      {statusCounters.canceled !== 0 && (
        <BadgeIcon
          icon={<FontAwesomeIcon icon={faX} />}
          title={t('Aborted')}
          badge={statusCounters.canceled.toString()}
          size="xs"
        />
      )}
    </>
  )

  const columns: TableColumnInstance<ProductColumns, keyof ProductColumns>[] = [
    {
      id: 'name',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Name')}
            sorting={
              <SortButton
                sort={sorting?.name}
                onSort={(direction) => handleSort('name', direction)}
              />
            }
          />
        </div>
      ),
      th: true,
    },
    {
      id: 'type',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Type')}
            sorting={
              <SortButton
                sort={sorting?.type}
                onSort={(direction) => handleSort('type', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ type }) => getProductTypeName(type),
    },
    {
      id: 'productClasses',
      header: t('Classes'),
      template: ({ type, productClasses }) =>
        getProductClassesList(type, productClasses),
    },
    { id: 'createdBy', header: t('Created by') },
    {
      id: 'creationDate',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Created on')}
            sorting={
              <SortButton
                sort={sorting?.creationDate}
                onSort={(direction) => handleSort('creationDate', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ creationDate }) => formatDate(creationDate),
    },
    {
      id: 'counterTraining',
      header: t('Trainings'),
      template: ({ counterTraining }) => renderStatusBadge(counterTraining),
    },
    {
      id: 'counterTrainingTest',
      header: t('Tests'),
      template: ({ counterTrainingTest }) =>
        renderStatusBadge(counterTrainingTest),
    },
    {
      id: 'actions',
      header: t('Actions'),
      noWrap: true,
      align: 'right',
      width: 0,
      template: useCallback(
        (p) => (
          <>
            <DownloadAnnotationsButton
              productId={p.id}
              disabled={isAnnotationsImportPending}
              title={t(
                isAnnotationsImportPending === false
                  ? 'Export annotations'
                  : 'An import is currently in progress, wait for it to finish'
              )}
            />
            <input id="productId" className={classes.fileChooser} />
            <input
              ref={inputRef}
              className={classes.fileChooser}
              type="file"
              accept=".zip"
              onClick={(e) => {
                e.stopPropagation()
              }}
              onChange={(e) => {
                const productId = (document.getElementById(
                  'productId'
                ) as HTMLInputElement).value
                handleFileChange(e, productId)
              }}
            />
            <Button
              onClick={(e) => {
                const input = document.getElementById(
                  'productId'
                ) as HTMLInputElement
                input.value = p.id
                inputRef.current?.click()
                e.stopPropagation()
              }}
              label={t('Import annotations')}
              hideLabel
              title={t(
                isAnnotationsImportPending === false
                  ? 'Import annotations'
                  : 'An import is currently in progress, wait for it to finish before starting a new one'
              )}
              icon={
                <FontAwesomeIcon
                  icon={faFileUpload}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(isAnnotationsImportPending ? { fade: true } : undefined)}
                />
              }
              buttonType="button"
              type="blank-default"
              disabled={isAnnotationsImportPending}
            />
            <Button
              onClick={(e) => {
                e.stopPropagation()
                setSelectedProduct(p)
                setProductsCreateUpdateModal(true)
              }}
              label={t('Edit')}
              hideLabel
              title={t(
                isAnnotationsImportPending === false
                  ? 'Edit'
                  : 'An import is currently in progress, wait for it to finish'
              )}
              icon={<FontAwesomeIcon icon={faCog} />}
              buttonType="button"
              type="blank-default"
              disabled={isAnnotationsImportPending}
            />

            <Button
              onClick={(e) => {
                e.stopPropagation()
                setSelectedProduct(p)
                setDeleteConfirmationModal(true)
              }}
              label={t('Delete')}
              hideLabel
              title={t(
                isAnnotationsImportPending === false
                  ? 'Delete'
                  : 'An import is currently in progress, wait for it to finish'
              )}
              icon={<FontAwesomeIcon icon={faTrash} />}
              disabled={isDeleteSubmitting || isAnnotationsImportPending}
              buttonType="button"
              type="blank-destroy"
            />
          </>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDeleteSubmitting, t, isAnnotationsImportPending]
      ),
    },
  ]

  return (
    <div id="divProductsListId">
      <div className={classes.header}>
        <FiltersModal<ProductFilters>
          initialValues={productFilters}
          labels={filterLabels}
          removeDisabled
          onSave={handleSaveFilters}
          onRemove={handleRemoveFilters}
        >
          {({ filters: f, setFilters }) => (
            <>
              <Input
                name="nameFilter"
                id="nameFilter"
                type="text"
                label={t('Name')}
                placeholder={t('Filter...')}
                value={f.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters((prev) => ({
                    ...prev,
                    name: e.target.value || undefined,
                  }))
                }
              />
              <AdvancedSelect
                name="typeFilter"
                id="typeFilter"
                isClearable
                label={t('Type')}
                placeholder={t('Filter...')}
                value={
                  f.type
                    ? {
                        value: f.type,
                        label: getProductTypeName(f.type as ProductType),
                      }
                    : null
                }
                onChange={(v: { value: string; label: string } | null) =>
                  setFilters((prev) => ({
                    ...prev,
                    type: v ? v.value : undefined,
                  }))
                }
                options={[
                  { value: 'anomaly', label: t('Anomaly') },
                  { value: 'classification', label: t('Classification') },
                  { value: 'segmentation', label: t('Segmentation') },
                  { value: 'classification_patch', label: t('Patch') },
                ]}
              />
            </>
          )}
        </FiltersModal>
      </div>
      <InfiniteScroll
        key={listKey}
        pageStart={-1}
        loadMore={handleLoadMore}
        hasMore={products ? products.results.length < products.count : true}
        loader={<LoadingBlock message={t('Loading')} key="loading" />}
      >
        <Table
          columns={columns}
          data={products ? products.results : []}
          alternateRows
          onRowClick={(p) => {
            history.push(getDetailsLocation(Number(projectId), p.id))
            setProduct(dispatch, p)
          }}
          isEmpty={
            products && products.count === 0 && products.results.length <= 0
          }
          emptyMessage={t(
            `There's nothing here. Try creating a new {{ itemName }}.`,
            { itemName: t('product') }
          )}
        />
        {products &&
          products.results.length >= products.count &&
          products.count > 0 && (
            <div className={classes.endOfList}>
              {t('All {{count}} records loaded', { count: products.count })}
            </div>
          )}
      </InfiniteScroll>

      {deleteConfirmationModal && selectedProduct && (
        <DeleteConfirmationModal
          onCloseRequest={() => setDeleteConfirmationModal(false)}
          onDeleteRequest={() =>
            deleteProduct(dispatch, selectedProduct.id, setDeleteSubmitting)
          }
        />
      )}

      {productsCreateUpdateModal && selectedProduct && (
        <ProductsCreateUpdate
          setModal={setProductsCreateUpdateModal}
          actionType="update"
          productId={selectedProduct.id}
          initialValues={{
            name: selectedProduct.name,
            classes: selectedProduct.productClasses,
            type: selectedProduct.type,
            description:
              lastDescriptionUpdated.productId === selectedProduct.id
                ? lastDescriptionUpdated.description
                : selectedProduct.description,
          }}
          setLastDescriptionUpdated={setLastDescriptionUpdated}
        />
      )}

      {showClassMapping && (
        <UploadAnnotationClassMappingModal
          onCloseRequest={() => {
            setShowClassMapping(false)
          }}
          onConfirmRequest={() => {
            setIsLoading(true)
            setShowClassMapping(false)
          }}
          setUploading={setIsLoading}
          productId={selectedProductId}
          sourceProductClasses={srcProductClasses}
          targetProductClasses={trgProductClasses}
          annotationFileToUpload={annotationFileToUpload}
        />
      )}

      {showOnlyObsDisclaimer && (
        <UploadAnnotationOnlyObservationModal
          onCloseRequest={() => {
            setShowOnlyObsDisclaimer(false)
          }}
          onConfirmRequest={() => {
            setIsLoading(true)
            setShowOnlyObsDisclaimer(false)
            handleSubmit(String(selectedProductId), annotationFileToUpload)
          }}
        />
      )}
    </div>
  )
}

export default ProductsList
